import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Calendar, Pencil, Filter } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { DataTable } from "@/components/datatable/data-table";
import { ColumnDef, Table } from "@tanstack/react-table";
import { TechnicianList } from "@/hooks/services/pps/handlers/technicians-api";
import { Technician } from "@/hooks/services/pps/types/technicians.type";
import { Badge } from "@/components/ui/badge";
import { useWorkSpaceStore } from "@/stores/store";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { ColumnVisibilityDropdown } from "@/components/datatable";
import { EditTechnicianSheet } from "../components/edit-technician-sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { useGetData } from "@/hooks/services/use-query-hooks";
import { ShopList } from "@/hooks/services/pps/handlers/shop-api";
import { useDebounce } from "@/hooks/use-debounce";
import { SearchFilterBar } from "@/components/search-filter-bar";

export const Route = createFileRoute(
  "/plant-3/(technician-screens)/technicians/list"
)({
  component: RouteComponent
});

function RouteComponent() {
  const workspaceId = useWorkSpaceStore((state) => state.workspace.id);
  const navigate = useNavigate();
  const [tableInstance, setTableInstance] = useState<Table<any> | null>(null);
  const [editingTechnician, setEditingTechnician] = useState<Technician | null>(
    null
  );
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [shopId, setShopId] = useState<number | null>(null);
  const debouncedSearch = useDebounce(searchInput, 300);

  const { data: shopsData } = useGetData(ShopList({ workspaceId }), {
    enabled: !!workspaceId && workspaceId > 0
  });

  const columns: ColumnDef<Technician>[] = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => (
          <div className="aero:!font-medium">{row.getValue("name")}</div>
        )
      },
      {
        id: "currentAvailability",
        header: "Current Availability",
        cell: ({ row }) => {
          const isAvail = row.original.isCurrentlyAvailable;
          if (isAvail == null) {
            return <Badge variant="secondary">N/A</Badge>;
          }
          return (
            <div className="aero:flex">
              <Badge
                className={
                  (isAvail
                    ? "aero:bg-green-100 aero:!text-green-800"
                    : "aero:bg-red-100 aero:!text-red-800") +
                  " aero:pointer-events-none aero:hover:bg-inherit aero:hover:!text-inherit"
                }
              >
                {isAvail ? "✓ Available" : "✗ Unavailable"}
              </Badge>
            </div>
          );
        }
      },
      {
        accessorKey: "employeeNumber",
        header: "Employee Number",
        cell: ({ row }) => (
          <div className="aero:!text-sm aero:!text-foreground">
            {row.getValue("employeeNumber") || "-"}
          </div>
        )
      },
      {
        accessorKey: "workspace",
        header: "Workspace",
        cell: ({ row }) => {
          const workspace = row.original.workspace;
          return (
            <Badge variant="outline">{workspace?.workspaceName || "N/A"}</Badge>
          );
        }
      },
      {
        accessorKey: "shop",
        header: "Shop",
        cell: ({ row }) => {
          const shop = row.original.shop;
          return (
            <Badge variant={shop ? "default" : "secondary"}>
              {shop?.shopName || "Unassigned"}
            </Badge>
          );
        }
      },
      {
        accessorKey: "machines",
        header: "Machines",
        cell: ({ row }) => {
          const machines = row.original.machines;
          return (
            <div className="aero:!text-sm aero:!text-foreground">
              {machines?.length || 0} machine(s)
            </div>
          );
        }
      }
    ],

    []
  );

  return (
    <>
      <SearchFilterBar
        placeholder="Search by name or employee no..."
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        addOnFilter={[
          <Select
            key="shop-filter"
            value={shopId != null ? String(shopId) : "all"}
            onValueChange={(val) =>
              setShopId(val === "all" ? null : Number(val))
            }
          >
            <SelectTrigger className="aero:w-[180px]">
              <Filter className="aero:mr-2 aero:h-4 aero:w-4" />
              <SelectValue placeholder="Filter by Shop" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Shops</SelectItem>
              {(shopsData?.data as any[])?.map((shop) => (
                <SelectItem key={shop.id} value={String(shop.id)}>
                  {shop.shopName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ]}
      />

      <Card className="aero:!rounded-lg aero:!border-neutral-200 aero:!p-0 aero:!gap-0 aero:!shadow-none aero:w-full aero:min-w-0 aero:!overflow-hidden">
        <CardHeader className="aero:!p-4">
          <div className="aero:flex aero:flex-col aero:lg:flex-row aero:items-start aero:justify-between aero:gap-4">
            <div>
              <h2 className="aero:text-base aero:md:text-lg aero:font-bold aero:leading-7 aero:tracking-normal aero:text-foreground">
                Technician List
              </h2>
              <p className="aero:text-xs aero:md:text-sm aero:text-neutral-500 aero:leading-5 aero:tracking-normal">
                View and manage technicians, their availability, shop
                assignments, and qualified machines
              </p>
            </div>

            <div className="aero:flex aero:items-center aero:gap-2 aero:flex-shrink-0">
              {tableInstance && (
                <ColumnVisibilityDropdown table={tableInstance} />
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="aero:!p-4 aero:min-w-0 aero:overflow-x-auto">
          <DataTable<Technician, any>
            api={TechnicianList}
            filterParams={{
              workspaceId,
              ...(debouncedSearch ? { search: debouncedSearch } : {}),
              ...(shopId != null ? { shopId } : {})
            }}
            onTableReady={(table) => {
              setTableInstance(table);
            }}
            columns={columns}
            identifierKey="id"
            features={{
              enableSelection: false,
              enableEditing: false,
              enablePinning: true,
              enableResizing: true,
              enableColumnReorder: false,
              enableRowDragAndDrop: false,
              enableSorting: true,
              enablePagination: true
            }}
            actionItems={[
              {
                title: "Edit",
                icon: <Pencil className="aero:h-5 aero:w-5" />,
                className: "aero:bg-blue-600",
                onClick: (row: Technician) => {
                  setEditingTechnician(row);
                  setEditSheetOpen(true);
                }
              },
              {
                title: "Schedule",
                icon: <Calendar className="aero:h-5 aero:w-5" />,
                className: "aero:bg-primary-700",
                onClick: (row: Technician) =>
                  navigate({
                    to: "/plant-3/technicians/schedule",
                    search: { technicianId: String(row.id) }
                  })
              }
            ]}
            pageSizes={[10, 20, 50, 100]}
            defaultPageSize={10}
            emptyState={"No technicians found"}
          />
        </CardContent>
      </Card>
      {editingTechnician && (
        <EditTechnicianSheet
          technician={editingTechnician}
          open={editSheetOpen}
          onOpenChange={setEditSheetOpen}
          onSuccess={() => {
            setEditSheetOpen(false);
            setEditingTechnician(null);
          }}
        />
      )}
    </>
  );
}
